// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import type { Brand } from '../brand.js';
import '../styles/brand-lockup.css';

/** Parent-brand run emphasised inside the endorsement line ("A Cobault
 *  strategy provider") — the parent's own name is a family-level
 *  constant, not counterparty data. */
const PARENT_RUN = 'Cobault';

/** Split `text` around the first occurrence of `run`, or null when the
 *  run is absent (callers then render `text` plain — the fail-safe for
 *  server copy that drifts from the expected shape). */
function splitRun(text: string, run: string): [string, string, string] | null {
  const i = text.indexOf(run);
  if (i < 0) return null;
  return [text.slice(0, i), run, text.slice(i + run.length)];
}

/** Two-tone wordmark per the agreed brand sheet: the treatment's accent
 *  run ("jade" in "Bluejade Markets") takes the accent colour, the rest
 *  stays Mist. Unknown ids have no accent run → plain wordmark. */
function Wordmark({ brand }: { brand: Brand }) {
  const name = brand.displayName ?? '';
  const runs = brand.accentRun ? splitRun(name, brand.accentRun) : null;
  return (
    <span class="brand-lockup-wordmark">
      {runs ? (
        <>
          {runs[0]}
          <span class="brand-lockup-accent-run">{runs[1]}</span>
          {runs[2]}
        </>
      ) : (
        name
      )}
    </span>
  );
}

/**
 * The full sibling-brand lockup (COB-394): the Cut mark + the two-tone
 * wordmark, with the endorsement line ("A Cobault strategy provider",
 * "Cobault" emphasised in Mist) beneath. Per the agreed brand sheet the
 * endorsement is used once per surface — agreement panels and terms
 * headers here; the offer card uses the compact `BrandPill` instead.
 *
 * Renders nothing on the legacy / no-descriptor path, so a consuming
 * surface stays byte-identical when inert.
 */
export function BrandLockup({
  brand,
  class: className,
}: {
  brand: Brand;
  class?: string;
}) {
  if (!brand.displayName && !brand.lockup) return null;
  const endorse = brand.lockup ? splitRun(brand.lockup, PARENT_RUN) : null;
  const marked = brand.markSrc ? ' brand-lockup--marked' : '';
  return (
    <span
      class={`brand-lockup${marked}${className ? ` ${className}` : ''}`}
      data-brand={brand.attr}
    >
      <span class="brand-lockup-row">
        {brand.markSrc && (
          <img class="brand-lockup-mark" src={brand.markSrc} alt="" width={20} height={20} />
        )}
        {brand.displayName && <Wordmark brand={brand} />}
      </span>
      {brand.lockup && (
        <span class="brand-lockup-endorse">
          {endorse ? (
            <>
              {endorse[0]}
              <span class="brand-lockup-parent">{endorse[1]}</span>
              {endorse[2]}
            </>
          ) : (
            brand.lockup
          )}
        </span>
      )}
    </span>
  );
}

/**
 * The compact branded pill from the agreed vault-card treatment: the Cut
 * gem + the display name on a Jade Deep fill (white text, AA). This is
 * the point-of-offer identity treatment — the offer card carries the
 * pill, not the full lockup. Outside a `data-brand` scope (unknown id)
 * the fill falls back to the stock Cobault accent-hover, honouring the
 * server copy without guessing a colourway.
 *
 * Renders nothing without a display name (the legacy path).
 */
export function BrandPill({
  brand,
  class: className,
}: {
  brand: Brand;
  class?: string;
}) {
  if (!brand.displayName) return null;
  return (
    <span
      class={className ? `brand-pill ${className}` : 'brand-pill'}
      data-brand={brand.attr}
    >
      {brand.markSrc && (
        <img class="brand-pill-gem" src={brand.markSrc} alt="" width={13} height={13} />
      )}
      {brand.displayName}
    </span>
  );
}
